/**
 * <s-favorites-grid> builds the Favorites page from the shopper's saved hearts (localStorage "tania:favorites",
 * newest first; see s-favorite-button.js). Each piece is fetched as the real collection card
 * (/products/<handle>?view=fav-card), so Quick Add, badges and prices match collection pages exactly.
 * Available pieces come first, sold-out pieces last. Unsaving a heart removes its card at once.
 * States on the element: data-state="loading" | "filled" | "empty". The count text fills [data-fav-count].
 * When filled, a related-products rail ([data-fav-related] template) is added for the newest saved piece.
 */
const KEY = 'tania:favorites';

function read() {
  try {
    const value = JSON.parse(localStorage.getItem(KEY) || '[]');
    return Array.isArray(value) ? value.filter((h) => typeof h === 'string') : [];
  } catch {
    return [];
  }
}

class SFavoritesGrid extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    window.addEventListener('tania:favorites-change', () => this.#prune().catch(() => {}), opts);
    window.addEventListener('storage', (event) => { if (event.key === KEY) this.#prune().catch(() => {}); }, opts);
    // Visible "Remove" link under each saved card (the heart also removes, but this is the obvious path).
    this.addEventListener('click', (event) => {
      const button = event.target instanceof Element ? event.target.closest('[data-fav-remove-btn]') : null;
      if (!button) return;
      event.preventDefault();
      const handle = button.closest('li[data-handle]')?.dataset.handle;
      if (!handle) return;
      const handles = read().filter((h) => h !== handle);
      try {
        localStorage.setItem(KEY, JSON.stringify(handles));
      } catch {
        // Storage unavailable: nothing to remove.
      }
      window.dispatchEvent(new CustomEvent('tania:favorites-change', { detail: { handles } }));
    }, opts);
    this.#load().catch(() => this.#setState());
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  get #list() {
    return this.querySelector('[data-fav-list]');
  }

  get #root() {
    return window.Shopify?.routes?.root || '/';
  }

  // One real collection card (templates/product.fav-card.liquid); falls back to a simple card if that view doesn't load.
  async #card(handle) {
    const html = await fetch(`${this.#root}products/${encodeURIComponent(handle)}?view=fav-card`)
      .then((response) => (response.ok ? response.text() : ''))
      .catch(() => '');
    const template = document.createElement('template');
    template.innerHTML = html;
    return template.content.querySelector('li[data-handle]') || this.#simpleCard(this.#root, handle);
  }

  async #load() {
    const handles = read().slice(0, 60);
    if (!handles.length) return this.#setState();
    const items = (await Promise.all(handles.map((handle) => this.#card(handle)))).filter(Boolean);
    const available = items.filter((li) => li.dataset.available === 'true');
    const soldOut = items.filter((li) => li.dataset.available !== 'true');
    items.forEach((li) => this.#addRemove(li));
    this.#list?.replaceChildren(...available, ...soldOut);
    this.#setState();
    this.#addRelated(items[0]);
  }

  #addRemove(li) {
    const title = li.querySelector('h3')?.textContent?.trim() || '';
    const button = document.createElement('button');
    button.type = 'button';
    button.dataset.favRemoveBtn = '';
    button.className = 'fav-remove mt-2 inline-flex cursor-pointer items-center gap-1.5 self-start text-[0.6875rem] uppercase tracking-[0.16em] text-on-surface-secondary underline underline-offset-4 hover:text-on-surface';
    button.textContent = this.dataset.removeLabel || 'Remove';
    button.setAttribute('aria-label', (this.dataset.favRemove || 'Remove __TITLE__ from Favorites').replace('__TITLE__', title));
    li.append(button);
  }

  async #simpleCard(root, handle) {
    const product = await fetch(`${root}products/${encodeURIComponent(handle)}.js`)
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);
    if (!product) return null;
    const esc = (value) => {
      const div = document.createElement('div');
      div.textContent = value ?? '';
      return div.innerHTML;
    };
    const money = (cents) => `$${(Number(cents) / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
    const image = product.featured_image ? `${product.featured_image.startsWith('//') ? 'https:' : ''}${product.featured_image}${product.featured_image.includes('?') ? '&' : '?'}width=600` : '';
    const onSale = product.compare_at_price > product.price;
    const icon = this.querySelector('template[data-heart-icon]')?.innerHTML || '';
    const add = esc((this.dataset.favAdd || '').replace('__TITLE__', product.title));
    const remove = esc((this.dataset.favRemove || '').replace('__TITLE__', product.title));
    const li = document.createElement('li');
    li.dataset.handle = product.handle;
    li.dataset.productId = String(product.id);
    li.dataset.available = String(product.available !== false);
    if (product.available === false) li.className = 'fav-sold-out';
    li.innerHTML = `
      <div class="relative">
        <a href="${esc(product.url)}" class="card-media group block">
          <div class="relative aspect-[2/3] overflow-hidden bg-surface-secondary">
            ${image ? `<img src="${esc(image)}" alt="${esc(product.title)}" loading="lazy" class="h-full w-full object-cover">` : ''}
            ${onSale && product.available !== false ? `<span class="pointer-events-none absolute top-2 inset-s-2 z-10 rounded-full px-2.5 py-1 text-[0.625rem] font-medium uppercase leading-none tracking-[0.16em]" style="background-color:#2B2523;color:#E9E4E0">${esc(this.dataset.saleLabel || 'Sale')}</span>` : ''}
            ${product.available === false ? `<span class="pointer-events-none absolute top-2 inset-s-2 z-10 rounded-full px-2.5 py-1 text-[0.625rem] font-medium uppercase leading-none tracking-[0.16em]" style="background-color:#FEFBFA;color:#2B2523">${esc(this.dataset.soldOutLabel || 'Sold Out')}</span>` : ''}
          </div>
        </a>
        <s-favorite-button handle="${esc(product.handle)}" data-label-add="${add}" data-label-remove="${remove}" class="absolute top-2 inset-e-2 z-20 block">
          <button type="button" aria-pressed="true" aria-label="${remove}" class="fav-heart fav-heart--card inline-flex cursor-pointer items-center justify-center text-[#2B2523]">
            <span class="inline-flex size-5 items-center justify-center [&_svg]:size-full" aria-hidden="true">${icon}</span>
          </button>
        </s-favorite-button>
      </div>
      <div class="mt-3 flex flex-col gap-1">
        <h3 class="type-body-sm text-on-surface line-clamp-2"><a href="${esc(product.url)}">${esc(product.title)}</a></h3>
        <p class="text-sm">${money(product.price)}${onSale ? ` <s style="text-decoration-color:#A8574C" class="text-on-surface-secondary">${money(product.compare_at_price)}</s>` : ''}</p>
      </div>`;
    return li;
  }

  // Keeps the page in step with hearts: unsaved pieces leave the grid; a piece saved from You May Also Like
  // moves up into the saved grid (with its Remove link).
  async #prune() {
    const saved = read();
    const savedSet = new Set(saved);
    this.#list?.querySelectorAll(':scope > li[data-handle]').forEach((li) => {
      if (!savedSet.has(li.dataset.handle)) li.remove();
    });
    const shown = new Set([...(this.#list?.querySelectorAll(':scope > li[data-handle]') || [])].map((li) => li.dataset.handle));
    const added = saved.filter((handle) => !shown.has(handle));
    const slow = [];
    for (const handle of added) {
      const selector = `li[data-handle="${CSS.escape(handle)}"]`;
      // Instant: move the card the shopper just hearted (You May Also Like) or copy it (New Arrivals in the empty state).
      const relatedCard = this.querySelector(`[data-fav-related-list] > ${selector}`);
      const starterCard = this.querySelector(`[data-fav-empty] ${selector}`);
      const card = relatedCard || (starterCard ? starterCard.cloneNode(true) : null);
      if (card) {
        card.dataset.available = card.dataset.available || 'true';
        this.#addRemove(card);
        this.#list?.prepend(card);
      } else {
        slow.push(handle);
      }
    }
    this.#setState();
    // First save on an empty page: bring in You May Also Like for it.
    const relatedSection = this.querySelector('[data-fav-related]');
    const newest = this.#list?.querySelector(':scope > li[data-product-id]');
    if (relatedSection?.hidden && newest && !this.dataset.relatedLoaded) this.#addRelated(newest).catch(() => {});
    // Saved elsewhere (e.g. another tab): fetch those cards.
    for (const handle of slow) {
      const card = await this.#card(handle);
      if (!card || !read().includes(handle) || this.#list?.querySelector(`:scope > li[data-handle="${CSS.escape(handle)}"]`)) continue;
      this.#addRemove(card);
      this.#list?.prepend(card);
    }
    this.#setState();
  }

  #setState() {
    const count = this.#list?.querySelectorAll(':scope > li').length || 0;
    this.dataset.state = count ? 'filled' : 'empty';
    const label = this.querySelector('[data-fav-count]');
    if (label) {
      const word = count === 1 ? this.dataset.one : this.dataset.other;
      label.textContent = count && word ? word.replace('__COUNT__', String(count)) : '';
    }
    const related = this.querySelector('[data-fav-related]');
    if (related && !related.querySelector('[data-fav-related-list] > li')) related.hidden = true;
  }

  // "You May Also Like": Shopify's related recommendations for the newest saved piece, shown as the same
  // collection cards (Quick Add, badges, dots, heart), skipping sold-out pieces and anything already saved.
  async #addRelated(first) {
    const section = this.querySelector('[data-fav-related]');
    const list = this.querySelector('[data-fav-related-list]');
    if (!section || !list || !first?.dataset.productId) return;
    this.dataset.relatedLoaded = 'true';
    const response = await fetch(`${this.#root}recommendations/products.json?product_id=${first.dataset.productId}&limit=10&intent=related`).catch(() => null);
    if (!response?.ok) return;
    const data = await response.json().catch(() => ({}));
    const saved = new Set(read());
    const handles = (data.products || [])
      .filter((product) => product.available !== false && !saved.has(product.handle))
      .slice(0, 4)
      .map((product) => product.handle);
    if (!handles.length) return;
    const cards = (await Promise.all(handles.map((handle) => this.#card(handle)))).filter(Boolean);
    if (!cards.length) return;
    list.replaceChildren(...cards);
    section.hidden = false;
  }
}

if (!customElements.get('s-favorites-grid')) customElements.define('s-favorites-grid', SFavoritesGrid);
